import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background text-foreground hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
        /** 保存・登録・送信・OK などの主操作（ティール／緑系・ブランド統一） */
        cta: "bg-teal-600 text-white hover:bg-teal-700 shadow-sm focus-visible:ring-teal-600/40",
        /** ダッシュボード共通: リセット・キャンセル（塗り） */
        ctaMuted: "bg-gray-200 text-gray-800 hover:bg-gray-300 shadow-sm focus-visible:ring-gray-400/30",
        /** スマホパネル「戻る」など強いナビ */
        navSheet:
          "rounded-md border border-neutral-900 bg-neutral-900 text-white hover:bg-neutral-800 shadow-sm focus-visible:ring-neutral-500/40",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9 rounded-md",
        /** パネルヘッダ横の「戻る」 */
        sheetNav: "h-auto rounded-md py-2 px-6 text-sm font-medium",
        /** 申し送りの音声・保存・リセット列（高さ可変） */
        handoverTile:
          "h-auto min-h-[52px] flex-1 flex-col gap-1 py-3 px-2 font-medium [&_span]:text-xs",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
